import { useCallback, useMemo, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { ProductsSequenceProduct } from 'common/contracts';

export type ProductEntry = ProductsSequenceProduct & { id: string };

interface IUsePopupProductsReturn {
  productsList: ProductEntry[];
  addProduct: (product: ProductsSequenceProduct) => void;
  deleteProduct: (id: string) => void;
  updateProduct: (id: string, product: ProductsSequenceProduct) => void;
  setDailyProducts: React.Dispatch<React.SetStateAction<ProductEntry[]>>;
  setPostProducts: React.Dispatch<React.SetStateAction<ProductEntry[]>>;
}

/**
 * Manages separate product lists for Daily Bonus and Post Purchase popup subtypes.
 *
 * @example
 * const { productsList, addProduct } = usePopupProducts(isDailyBonus);
 */
export const usePopupProducts = (isDailyBonus: boolean): IUsePopupProductsReturn => {
  const [dailyProducts, setDailyProducts] = useState<ProductEntry[]>([]);
  const [postProducts, setPostProducts] = useState<ProductEntry[]>([]);

  const productsList = useMemo(
    (): ProductEntry[] => (isDailyBonus ? dailyProducts : postProducts),
    [isDailyBonus, dailyProducts, postProducts]
  );

  const addProduct = useCallback(
    (newProduct: ProductsSequenceProduct): void => {
      const entry: ProductEntry = { ...newProduct, id: uuidv4() };
      if (isDailyBonus) {
        setDailyProducts((prev) => [...prev, entry]);
      } else {
        setPostProducts([entry]);
      }
    },
    [isDailyBonus]
  );

  const deleteProduct = useCallback(
    (id: string): void => {
      if (isDailyBonus) {
        setDailyProducts((prev) => prev.filter((p) => p.id !== id));
      } else {
        setPostProducts((prev) => prev.filter((p) => p.id !== id));
      }
    },
    [isDailyBonus]
  );

  const updateProduct = useCallback(
    (id: string, newProductData: ProductsSequenceProduct): void => {
      if (isDailyBonus) {
        setDailyProducts((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, ...newProductData } : p
          )
        );
      } else {
        setPostProducts((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, ...newProductData } : p
          )
        );
      }
    },
    [isDailyBonus]
  );

  return {
    productsList,
    addProduct,
    deleteProduct,
    updateProduct,
    setDailyProducts,
    setPostProducts
  };
};
